import {
  App,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Table,
  Tag,
} from 'antd';
import type { TableProps } from 'antd';
import { EditOutlined, PlusOutlined } from '@ant-design/icons';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import {
  setupApi,
  type SchoolClass,
  type ClassDisplayMode,
} from '@/api/setup';
import { useAuthStore } from '@/store/auth';
import { PageHeader } from '@/shared/components/page-header';
import { extractError } from '@/shared/lib/error-message';

const QK = ['setup', 'classes'] as const;

const DISPLAY_MODE_LABEL: Record<ClassDisplayMode, string> = {
  GRADES_ONLY: 'Grades only',
  PERCENTAGE_ONLY: 'Percentage only',
  BOTH: 'Both',
};

interface FormShape {
  name: string;
  sortOrder: number;
  displayMode: ClassDisplayMode;
}

export function ClassesPage() {
  const { message } = App.useApp();
  const qc = useQueryClient();
  const canCreate = useAuthStore((s) => s.hasAuthority('SETUP:CREATE'));
  const canEdit = useAuthStore((s) => s.hasAuthority('SETUP:EDIT'));

  const [editing, setEditing] = useState<SchoolClass | null>(null);
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm<FormShape>();

  const { data, isLoading } = useQuery({
    queryKey: QK,
    queryFn: () => setupApi.listClasses(false),
  });

  const upsert = useMutation({
    mutationFn: async (values: FormShape) => {
      const body = {
        name: values.name.trim(),
        sortOrder: values.sortOrder,
        displayMode: values.displayMode,
      };
      return editing
        ? setupApi.updateClass(editing.id, body)
        : setupApi.createClass(body);
    },
    onSuccess: () => {
      message.success(editing ? 'Class updated' : 'Class created');
      qc.invalidateQueries({ queryKey: QK });
      setOpen(false);
      setEditing(null);
      form.resetFields();
    },
    onError: (e) => message.error(extractError(e)),
  });

  const toggle = useMutation({
    mutationFn: ({ id, value }: { id: number; value: boolean }) => setupApi.toggleClass(id, value),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
    },
    onError: (e) => message.error(extractError(e)),
  });

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    const next = (data?.length ?? 0) + 1;
    form.setFieldsValue({ sortOrder: next, displayMode: 'BOTH' });
    setOpen(true);
  };

  const openEdit = (row: SchoolClass) => {
    setEditing(row);
    form.setFieldsValue({
      name: row.name,
      sortOrder: row.sortOrder,
      displayMode: row.displayMode,
    });
    setOpen(true);
  };

  const columns: TableProps<SchoolClass>['columns'] = [
    {
      title: 'Sort',
      dataIndex: 'sortOrder',
      width: 70,
      render: (n: number) => <Tag>{n}</Tag>,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      render: (n: string) => <span style={{ fontWeight: 600 }}>{n}</span>,
    },
    {
      title: 'Display mode',
      dataIndex: 'displayMode',
      width: 200,
      render: (m: ClassDisplayMode) => DISPLAY_MODE_LABEL[m],
    },
    {
      title: 'Active',
      dataIndex: 'active',
      width: 90,
      render: (active: boolean, row) => (
        <Switch
          size="small"
          checked={active}
          disabled={!canEdit || (toggle.isPending && toggle.variables?.id === row.id)}
          onChange={(v) => toggle.mutate({ id: row.id, value: v })}
        />
      ),
    },
    {
      title: 'Actions',
      width: 110,
      align: 'right',
      render: (_, row) =>
        canEdit ? (
          <Button size="small" type="text" icon={<EditOutlined />} onClick={() => openEdit(row)}>
            Edit
          </Button>
        ) : null,
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 20 }}>
      <PageHeader
        eyebrow="Setup / Academic"
        title="Classes"
        description="Grade levels carried by the school. Sort order controls where each class appears in lists."
        count={{ value: data?.length ?? 0, label: 'classes' }}
        actions={
          canCreate ? (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              New class
            </Button>
          ) : null
        }
      />

      <Table
        rowKey="id"
        size="middle"
        loading={isLoading}
        columns={columns}
        dataSource={data ?? []}
        pagination={false}
      />

      <Modal
        title={editing ? `Edit ${editing.name}` : 'New class'}
        open={open}
        onCancel={() => {
          setOpen(false);
          setEditing(null);
        }}
        onOk={() => form.submit()}
        confirmLoading={upsert.isPending}
        okText={editing ? 'Save' : 'Create'}
        destroyOnHidden
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(values) => upsert.mutate(values)}
        >
          <Form.Item
            label="Name"
            name="name"
            rules={[
              { required: true, message: 'Name is required' },
              { max: 50 },
            ]}
          >
            <Input placeholder="e.g. Grade 5" autoFocus />
          </Form.Item>
          <Space.Compact block>
            <Form.Item
              label="Sort order"
              name="sortOrder"
              rules={[{ required: true, message: 'Required' }]}
              style={{ flex: 1 }}
            >
              <InputNumber min={0} max={32_000} style={{ width: '100%' }} />
            </Form.Item>
            <Form.Item
              label="Display mode"
              name="displayMode"
              rules={[{ required: true }]}
              style={{ flex: 2, marginInlineStart: 12 }}
            >
              <Select
                options={(Object.keys(DISPLAY_MODE_LABEL) as ClassDisplayMode[]).map((k) => ({
                  value: k,
                  label: DISPLAY_MODE_LABEL[k],
                }))}
              />
            </Form.Item>
          </Space.Compact>
        </Form>
      </Modal>
    </div>
  );
}
